import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import {
  IMPACT_METRIC_GROUPS,
  type ImpactMetricDisplay,
  type ImpactMetricGroup,
  type ImpactMetricsData,
} from "./flowData";
import { STATIC_IMPACT_METRICS } from "./impactMetrics.static";

interface ImpactMetricsProps {
  mode: "live" | "static";
}

/* ------------------------------------------------------------------ */
/* Per-group accent theming (ties into the page's phase colors)        */
/* ------------------------------------------------------------------ */

interface GroupAccent {
  /** group-title accent bar */
  bar: string;
  /** icon chip background */
  iconBg: string;
  /** icon glyph color */
  iconText: string;
  /** always-on accent border */
  border: string;
  /** always-on corner glow */
  glow: string;
}

// Coverage & scale → blue (Digital Mapping); also the fallback accent.
const BLUE_ACCENT: GroupAccent = {
  bar: "bg-blue-500",
  iconBg: "bg-blue-100 dark:bg-blue-500/15",
  iconText: "text-blue-600 dark:text-blue-300",
  border: "border-blue-200 dark:border-blue-800/60",
  glow: "bg-blue-400/25",
};

const GROUP_ACCENTS: Record<string, GroupAccent> = {
  // Discovery funnel — the AI does the work → violet, the page's "magic" color
  discovery: {
    bar: "bg-violet-500",
    iconBg: "bg-violet-100 dark:bg-violet-500/15",
    iconText: "text-violet-600 dark:text-violet-300",
    border: "border-violet-200 dark:border-violet-800/60",
    glow: "bg-violet-400/25",
  },
  coverage: BLUE_ACCENT,
  // Physical mapping output → amber (Physical Mapping)
  physical: {
    bar: "bg-amber-500",
    iconBg: "bg-amber-100 dark:bg-amber-500/15",
    iconText: "text-amber-600 dark:text-amber-300",
    border: "border-amber-200 dark:border-amber-800/60",
    glow: "bg-amber-400/25",
  },
  // Validation & delivery → emerald (Export)
  validation: {
    bar: "bg-emerald-500",
    iconBg: "bg-emerald-100 dark:bg-emerald-500/15",
    iconText: "text-emerald-600 dark:text-emerald-300",
    border: "border-emerald-200 dark:border-emerald-800/60",
    glow: "bg-emerald-400/25",
  },
};

const FALLBACK_ACCENT: GroupAccent = BLUE_ACCENT;

/** Format a single metric value for display. */
function formatMetricValue(
  value: number,
  kind: ImpactMetricDisplay["kind"],
): string {
  if (kind === "percent") {
    return `${Math.round(value * 100)}%`;
  }
  return value.toLocaleString();
}

/* ------------------------------------------------------------------ */
/* Card                                                                */
/* ------------------------------------------------------------------ */

interface MetricCardProps {
  icon: React.ElementType;
  label: string;
  value: string;
  subtext?: string;
  accent: GroupAccent;
}

function MetricCard({ icon: Icon, label, value, subtext, accent }: MetricCardProps) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl border bg-card p-5 shadow-sm shadow-black/5",
        "transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md",
        accent.border,
      )}
    >
      {/* accent corner glow — always on */}
      <div
        className={cn(
          "pointer-events-none absolute -right-8 -top-8 size-24 rounded-full blur-2xl",
          accent.glow,
        )}
      />

      <span
        className={cn(
          "inline-flex size-11 items-center justify-center rounded-xl",
          accent.iconBg,
        )}
      >
        <Icon className={cn("size-5", accent.iconText)} />
      </span>

      <p className="mt-4 text-3xl font-bold leading-none tracking-tight tabular-nums text-foreground">
        {value}
      </p>
      <p className="mt-2 text-sm font-medium text-foreground/80">{label}</p>
      {subtext && (
        <p className="mt-0.5 text-xs text-muted-foreground">{subtext}</p>
      )}
    </div>
  );
}

/** Render one metric card from its display metadata + the resolved data set. */
function renderMetricCard(
  metric: ImpactMetricDisplay,
  data: ImpactMetricsData,
  accent: GroupAccent,
) {
  const value = formatMetricValue(data[metric.key], metric.kind);

  let subtext: string | undefined;
  if (metric.subtextKey && metric.subtextTemplate) {
    const subtextValue = data[metric.subtextKey].toLocaleString();
    subtext = metric.subtextTemplate.replace("{value}", subtextValue);
  }

  return (
    <MetricCard
      key={metric.key}
      icon={metric.icon}
      label={metric.label}
      value={value}
      subtext={subtext}
      accent={accent}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Group header + layout                                               */
/* ------------------------------------------------------------------ */

function GroupHeader({ group, accent }: { group: ImpactMetricGroup; accent: GroupAccent }) {
  return (
    <div>
      <div className="flex items-center gap-2.5">
        <span className={cn("h-5 w-1.5 rounded-full", accent.bar)} />
        <h3 className="text-lg font-semibold text-foreground">{group.title}</h3>
      </div>
      <p className="mt-1.5 pl-4 text-sm text-muted-foreground">{group.blurb}</p>
    </div>
  );
}

/** Themed loading skeleton matching the grid layout. */
function MetricsSkeleton() {
  return (
    <div className="space-y-10">
      {IMPACT_METRIC_GROUPS.map((group) => {
        const accent = GROUP_ACCENTS[group.id] ?? FALLBACK_ACCENT;
        return (
          <section key={group.id} className="space-y-4">
            <GroupHeader group={group} accent={accent} />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {group.metrics.map((metric) => (
                <div
                  key={metric.key}
                  className="h-[140px] animate-pulse rounded-xl bg-muted"
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

/** Render the four metric groups from a resolved data set. */
function MetricsGroups({ data }: { data: ImpactMetricsData }) {
  return (
    <div className="space-y-10">
      {IMPACT_METRIC_GROUPS.map((group) => {
        const accent = GROUP_ACCENTS[group.id] ?? FALLBACK_ACCENT;
        return (
          <section key={group.id} className="space-y-4">
            <GroupHeader group={group} accent={accent} />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {group.metrics.map((metric) =>
                renderMetricCard(metric, data, accent),
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}

/**
 * Impact-metrics grid for the "How it works" page.
 *
 * - `live`: pulls real numbers from `analytics.impactMetrics` (authed).
 * - `static`: renders the frozen public snapshot (`STATIC_IMPACT_METRICS`).
 */
export function ImpactMetrics({ mode }: ImpactMetricsProps) {
  if (mode === "static") {
    return <MetricsGroups data={STATIC_IMPACT_METRICS} />;
  }
  return <LiveImpactMetrics />;
}

function LiveImpactMetrics() {
  const { data, isLoading } = trpc.analytics.impactMetrics.useQuery();

  if (isLoading) {
    return <MetricsSkeleton />;
  }
  if (!data) {
    return null;
  }
  return <MetricsGroups data={data} />;
}

export default ImpactMetrics;
